import api, { ApiResponse } from "../api";
import { TokenRequest } from "./TokenRequest";
import { TokenInfo } from "./TokenInfo";
import { PaymentResult } from "./PaymentResult";
import {
  AchBankDetails,
  PaymentDetail,
  PaymentMethodSettings,
  PaymentMethodsResponse,
  PaymentReceipt,
  PaymentSessionRequest,
  PaymentSessionResponse,
  PaymentStatusResponse,
  PaymentSummary,
  PaymentUiSettingChangeLogEntry,
  SpringPage,
  SurchargeWarningResponse,
  AccountSurchargeWaiver,
  AccountSurchargeApplication,
  PaymentAggregates,
  PaymentHealth,
} from "./PaymentSession";
import { PendingPayment } from "./PendingPayment";

export interface PaymentSearchParams {
  accountNumber?: number;
  status?: string;
  provider?: string;
  invoiceNumber?: number;
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  size?: number;
}

class PaymentService {
  private static instance: PaymentService;
  private communicatorUrl = `${process.env.REACT_APP_DOMAIN}/paymentCallback.html`;

  private constructor() {}

  static getInstance(): PaymentService {
    if (!PaymentService.instance) {
      PaymentService.instance = new PaymentService();
    }

    return PaymentService.instance;
  }

  public getPaymentInfo = async (
    requestModel: TokenRequest
  ): Promise<ApiResponse<TokenInfo>> => {
    requestModel.hostedPaymentIFrameCommunicatorUrl = this.communicatorUrl;
    return await api.post("/payments/token", requestModel);
  };

  public createSession = async (
    request: PaymentSessionRequest
  ): Promise<ApiResponse<PaymentSessionResponse>> => {
    // Authorize.Net may be used for eCheck or credit card (non-surcharge path)
    request.hostedPaymentIFrameCommunicatorUrl = this.communicatorUrl;
    return await api.post("/payments/sessions", request);
  };

  public confirmPayment = async (
    paymentId: string,
    providerReturnStatus?: string | null
  ): Promise<ApiResponse<PaymentStatusResponse>> => {
    const query =
      providerReturnStatus && providerReturnStatus.trim()
        ? `?providerReturnStatus=${encodeURIComponent(providerReturnStatus.trim())}`
        : "";
    return await api.post(`/payments/${paymentId}/confirm${query}`, {});
  };

  public getPaymentStatus = async (
    paymentId: string
  ): Promise<ApiResponse<PaymentStatusResponse>> => {
    return await api.get(`/payments/${paymentId}`);
  };

  public getPaymentReceipt = async (
    paymentId: string
  ): Promise<ApiResponse<PaymentReceipt>> => {
    return await api.get(`/payments/${paymentId}/receipt`);
  };

  public getPaymentReceiptPdf = async (paymentId: string): Promise<ApiResponse<any>> => {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return await api.get(`/payments/${paymentId}/receipt.pdf`, {
      responseType: "blob",
      params: timeZone ? { timeZone } : undefined,
    });
  };

  public getPaymentDetail = async (
    paymentId: string
  ): Promise<ApiResponse<PaymentDetail>> => {
    return await api.get(`/payments/${paymentId}/detail`);
  };

  public listPayments = async (
    params: PaymentSearchParams
  ): Promise<ApiResponse<SpringPage<PaymentSummary>>> => {
    const q = new URLSearchParams();
    if (params.accountNumber != null) q.set("accountNumber", String(params.accountNumber));
    if (params.status) q.set("status", params.status);
    if (params.provider) q.set("provider", params.provider);
    if (params.invoiceNumber != null) q.set("invoiceNumber", String(params.invoiceNumber));
    if (params.dateFrom) q.set("dateFrom", params.dateFrom);
    if (params.dateTo) q.set("dateTo", params.dateTo);
    q.set("page", String(params.page ?? 0));
    q.set("size", String(params.size ?? 25));
    q.set("sort", "dateCreated,desc");
    return await api.get(`/payments?${q.toString()}`);
  };

  public adminSearchPayments = async (
    params: PaymentSearchParams
  ): Promise<ApiResponse<SpringPage<PaymentSummary>>> => {
    const q = new URLSearchParams();
    if (params.accountNumber != null) q.set("accountNumber", String(params.accountNumber));
    if (params.status) q.set("status", params.status);
    if (params.provider) q.set("provider", params.provider);
    if (params.invoiceNumber != null) q.set("invoiceNumber", String(params.invoiceNumber));
    if (params.dateFrom) q.set("dateFrom", params.dateFrom);
    if (params.dateTo) q.set("dateTo", params.dateTo);
    q.set("page", String(params.page ?? 0));
    q.set("size", String(params.size ?? 50));
    q.set("sort", "dateCreated,desc");
    return await api.get(`/payments/admin/search?${q.toString()}`);
  };

  public adminAggregates = async (params: {
    accountNumber?: number;
    provider?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<ApiResponse<PaymentAggregates>> => {
    const q = new URLSearchParams();
    if (params.accountNumber != null) q.set("accountNumber", String(params.accountNumber));
    if (params.provider) q.set("provider", params.provider);
    if (params.dateFrom) q.set("dateFrom", params.dateFrom);
    if (params.dateTo) q.set("dateTo", params.dateTo);
    const qs = q.toString();
    return await api.get(`/payments/admin/aggregates${qs ? `?${qs}` : ""}`);
  };

  public adminHealth = async (hours?: number): Promise<ApiResponse<PaymentHealth>> => {
    const q = new URLSearchParams();
    if (hours != null) q.set("hours", String(hours));
    const qs = q.toString();
    return await api.get(`/payments/admin/health${qs ? `?${qs}` : ""}`);
  };

  public getActivePayments = async (
    invoiceNumbers: number[]
  ): Promise<ApiResponse<Record<string, PendingPayment>>> => {
    const params = invoiceNumbers.map((n) => `invoiceNumbers=${n}`).join("&");
    return await api.get(`/payments/active?${params}`);
  };

  public getAvailablePaymentMethods = async (
    invoiceNumbers?: number[]
  ): Promise<ApiResponse<PaymentMethodsResponse>> => {
    if (invoiceNumbers?.length) {
      const params = invoiceNumbers.map((n) => `invoiceNumbers=${n}`).join("&");
      return await api.get(`/payments/settings/methods?${params}`);
    }
    return await api.get("/payments/settings/methods");
  };

  public getPaymentMethodSettings = async (): Promise<
    ApiResponse<PaymentMethodSettings>
  > => {
    return await api.get("/payments/settings/method-config");
  };

  public updatePaymentMethodSettings = async (
    payload: Partial<PaymentMethodSettings>
  ): Promise<ApiResponse<PaymentMethodSettings>> => {
    return await api.put("/payments/settings/method-config", payload);
  };

  public listAccountSurchargeApplications = async (): Promise<
    ApiResponse<AccountSurchargeApplication[]>
  > => {
    return await api.get("/payments/settings/surcharge-applications");
  };

  public addAccountSurchargeApplication = async (
    accountNumber: number
  ): Promise<ApiResponse<AccountSurchargeApplication>> => {
    return await api.put(`/payments/settings/surcharge-applications/${accountNumber}`, {});
  };

  public removeAccountSurchargeApplication = async (
    accountNumber: number
  ): Promise<ApiResponse<void>> => {
    return await api.delete(`/payments/settings/surcharge-applications/${accountNumber}`);
  };

  public getSurchargeWarning = async (
    invoiceNumbers?: number[]
  ): Promise<ApiResponse<SurchargeWarningResponse>> => {
    if (invoiceNumbers?.length) {
      const params = invoiceNumbers.map((n) => `invoiceNumbers=${n}`).join("&");
      return await api.get(`/payments/settings/surcharge-warning?${params}`);
    }
    return await api.get("/payments/settings/surcharge-warning");
  };

  public updateSurchargeWarning = async (
    payload: { message?: string; enabled?: boolean }
  ): Promise<ApiResponse<SurchargeWarningResponse>> => {
    return await api.put("/payments/settings/surcharge-warning", payload);
  };

  public updateCcSurchargeEnabled = async (
    enabled: boolean
  ): Promise<ApiResponse<SurchargeWarningResponse>> => {
    return await api.put("/payments/settings/surcharge-warning", { enabled });
  };

  public listAccountSurchargeWaivers = async (): Promise<
    ApiResponse<AccountSurchargeWaiver[]>
  > => {
    return await api.get("/payments/settings/surcharge-waivers");
  };

  public addAccountSurchargeWaiver = async (
    accountNumber: number
  ): Promise<ApiResponse<AccountSurchargeWaiver>> => {
    return await api.put(`/payments/settings/surcharge-waivers/${accountNumber}`, {});
  };

  public removeAccountSurchargeWaiver = async (
    accountNumber: number
  ): Promise<ApiResponse<void>> => {
    return await api.delete(`/payments/settings/surcharge-waivers/${accountNumber}`);
  };

  public getAchBankDetails = async (): Promise<ApiResponse<AchBankDetails>> => {
    return await api.get("/payments/settings/ach-bank-details");
  };

  public updateAchBankDetails = async (
    details: AchBankDetails
  ): Promise<ApiResponse<AchBankDetails>> => {
    return await api.put("/payments/settings/ach-bank-details", details);
  };

  public getPaymentSettingsChangeLog = async (): Promise<
    ApiResponse<PaymentUiSettingChangeLogEntry[]>
  > => {
    return await api.get("/payments/settings/change-log");
  };

  public savePayment = async (
    requestModel: PaymentResult
  ): Promise<ApiResponse<TokenInfo>> => {
    return await api.post("/payments/processResponse", requestModel);
  };
}

export default PaymentService;
